// src/components/roads/RoadAiPrediction.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — AI prediction UI for a single road
//
//   <RoadAiPredictionBadge prediction onClick />   compact table cell
//   <RoadAiPredictionDetail road prediction onAction />   full explainable card
//
// Both take the view-model from roadAiPredictions.getRoadPrediction(road).
// Everything shown is explained in plain language (FieldHint popovers), and the
// UI is explicit about where a number came from: ML model, rule-based estimate,
// or no prediction.
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, Progress, SimpleGrid, Text, Tooltip, VStack,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    MdOutlineAutoAwesome, MdTimeline, MdBolt, MdBuild, MdSchedule, MdAssessment, MdEdit,
    MdMap, MdVisibility, MdErrorOutline, MdWarning, MdCheckCircle, MdOutlineTipsAndUpdates,
    MdFiberManualRecord, MdArrowUpward,
} from 'react-icons/md';
import { FieldHint } from './AiRoadHelper';
import { RUL_BANDS, ACTION_INTENTS } from './roadAiPredictions';

const LIFE_SCALE_YEARS = 20;

const INTENT_ICON = {
    [ACTION_INTENTS.CREATE_WO]: MdBuild,
    [ACTION_INTENTS.SCHEDULE_INSPECTION]: MdSchedule,
    [ACTION_INTENTS.ASSESS]: MdAssessment,
    [ACTION_INTENTS.EDIT]: MdEdit,
    [ACTION_INTENTS.MAP]: MdMap,
    [ACTION_INTENTS.RUN_AI]: MdOutlineAutoAwesome,
    [ACTION_INTENTS.VIEW]: MdVisibility,
};
const PRIORITY_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray' };
const INSIGHT_ICON = { critical: MdErrorOutline, high: MdWarning, warning: MdWarning, medium: MdWarning, info: MdOutlineTipsAndUpdates, low: MdOutlineTipsAndUpdates, ok: MdCheckCircle, success: MdCheckCircle };
const INSIGHT_COLOR = { critical: 'red.400', high: 'orange.400', warning: 'orange.400', medium: 'yellow.500', info: 'blue.400', low: 'blue.400', ok: 'green.400', success: 'green.400' };

const fmtDate = (d) => {
    if (!d) return '—';
    const t = d instanceof Date ? d : new Date(d);
    return Number.isFinite(t.getTime()) ? t.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—';
};
const fmtDateTime = (d) => {
    if (!d) return null;
    const t = new Date(d);
    return Number.isFinite(t.getTime()) ? t.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Compact table cell
// ─────────────────────────────────────────────────────────────────────────────
export const RoadAiPredictionBadge = memo(({ prediction, onClick }) => {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    if (!prediction) return null;
    const { source, rulYears, band, confidence, riskScore, riskLevel, readiness, live } = prediction;

    const tip = (
        <Box fontSize="xs" maxW="260px">
            <Text fontWeight="800" mb={1}>{source === 'model' ? 'AI predicted remaining life' : source === 'estimate' ? 'Rule-based estimate (no ML prediction yet)' : 'No prediction'}</Text>
            <Text mb={1}>{prediction.summary}</Text>
            <Text opacity={0.8}>Risk index {riskScore} ({riskLevel.label}) · Data readiness {readiness.score}%{confidence != null ? ` · Confidence ${Math.round(confidence * 100)}%` : ''}</Text>
            {onClick && <Text mt={1} opacity={0.7}>Click for the full explanation.</Text>}
        </Box>
    );

    return (
        <Tooltip label={tip} hasArrow placement="left" openDelay={200}>
            <VStack
                as={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined}
                spacing={1} align="flex-end" cursor={onClick ? 'pointer' : 'help'} onClick={onClick}
                aria-label={onClick ? 'Show AI prediction details' : undefined}
                bg="transparent" border="none" p={0} w="full"
            >
                <HStack spacing={1}>
                    {live && <Icon as={MdFiberManualRecord} color="green.400" boxSize={2.5} aria-label="Live update" />}
                    {source === 'none' ? (
                        <Text color={muted} fontSize="xs">—</Text>
                    ) : (
                        <Badge
                            colorScheme={band?.color || 'gray'}
                            variant={source === 'model' ? 'subtle' : 'outline'}
                            borderStyle={source === 'estimate' ? 'dashed' : 'solid'}
                            borderRadius="full" px={2} fontSize="2xs"
                        >
                            {source === 'estimate' ? '~' : ''}{rulYears} yrs{source === 'estimate' ? ' est.' : ''}
                        </Badge>
                    )}
                    <Badge colorScheme={riskLevel.color} variant="solid" borderRadius="md" fontSize="2xs" minW="26px" textAlign="center">
                        {riskScore}
                    </Badge>
                </HStack>
                <HStack spacing={1} w="72px">
                    <Progress value={readiness.score} size="xs" flex={1} borderRadius="full" bg={track}
                        colorScheme={readiness.grade.color} aria-label={`Data readiness ${readiness.score}%`} />
                    <Text fontSize="2xs" color={muted} fontFamily="mono">{readiness.score}%</Text>
                </HStack>
            </VStack>
        </Tooltip>
    );
});
RoadAiPredictionBadge.displayName = 'RoadAiPredictionBadge';

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle bar — where the road sits on the RUL bands
// ─────────────────────────────────────────────────────────────────────────────
const LifeBar = memo(({ prediction }) => {
    const marker = useColorModeValue('gray.800', 'white');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const { rulYears, lower, upper } = prediction;
    const pct = (y) => `${Math.min(100, Math.max(0, (y / LIFE_SCALE_YEARS) * 100))}%`;
    let prev = 0;
    const segs = RUL_BANDS.map(b => {
        const end = Math.min(LIFE_SCALE_YEARS, b.max === Infinity ? LIFE_SCALE_YEARS : b.max);
        const seg = { ...b, from: prev, to: end };
        prev = end;
        return seg;
    });
    return (
        <Box>
            <Box position="relative" h="10px" borderRadius="full" overflow="hidden" display="flex">
                {segs.map(s => <Box key={s.key} h="full" w={`${((s.to - s.from) / LIFE_SCALE_YEARS) * 100}%`} bg={`${s.color}.300`} opacity={0.75} />)}
                {lower != null && upper != null && (
                    <Box position="absolute" top="2px" bottom="2px" left={pct(lower)} w={`calc(${pct(upper)} - ${pct(lower)})`} bg="blackAlpha.400" borderRadius="full" />
                )}
            </Box>
            {rulYears != null && (
                <Box position="relative" h="14px">
                    <Box position="absolute" left={pct(rulYears)} transform="translateX(-50%)" top="-4px" textAlign="center">
                        <Icon as={MdArrowUpward} color={marker} boxSize={3.5} />
                    </Box>
                </Box>
            )}
            <Flex justify="space-between" fontSize="2xs" color={muted} mt={rulYears != null ? 0 : 1}>
                <Text>0</Text><Text>2</Text><Text>5</Text><Text>8</Text><Text>{LIFE_SCALE_YEARS}+ yrs</Text>
            </Flex>
        </Box>
    );
});
LifeBar.displayName = 'LifeBar';

// ─────────────────────────────────────────────────────────────────────────────
// Full card
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {object}   prediction  from getRoadPrediction(road)
 * @param {(intent: string, action: object) => void} [onAction]
 * @param {boolean}  [isRunningAI]
 */
export const RoadAiPredictionDetail = memo(({ prediction, onAction, isRunningAI, showInputs = true }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('purple.100', 'whiteAlpha.200');
    const subtle = useColorModeValue('gray.50', 'whiteAlpha.50');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const headBg = useColorModeValue('linear(135deg, purple.50, white 60%, blue.50)', 'linear(135deg, rgba(128,90,213,0.16), gray.800 60%, rgba(49,130,206,0.12))');
    if (!prediction) return null;

    const p = prediction;
    const sourceBadge = p.source === 'model'
        ? <Badge colorScheme="purple" fontSize="2xs">ML model{p.modelVersion ? ` · ${p.modelVersion}` : ''}</Badge>
        : p.source === 'estimate'
            ? <HStack spacing={0}><Badge colorScheme="gray" variant="outline" fontSize="2xs">Rule-based estimate</Badge><FieldHint metric="rul_estimate" /></HStack>
            : <Badge colorScheme="gray" fontSize="2xs">No prediction</Badge>;

    return (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" overflow="hidden" role="region" aria-label="AI road prediction">
            {/* Headline */}
            <Box bgGradient={headBg} p={4}>
                <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
                    <HStack spacing={3} align="flex-start">
                        <Flex w={10} h={10} borderRadius="xl" bgGradient="linear(135deg, purple.500, blue.500)" align="center" justify="center" flexShrink={0}>
                            <Icon as={MdTimeline} color="white" boxSize={5} />
                        </Flex>
                        <Box>
                            <HStack spacing={1}>
                                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wide" color={muted}>Remaining useful life</Text>
                                <FieldHint metric="rul" />
                            </HStack>
                            <HStack spacing={2} align="baseline">
                                <Text fontSize="3xl" fontWeight="900" lineHeight="1" color={p.band ? `${p.band.color}.500` : muted}>
                                    {p.rulYears != null ? `${p.source === 'estimate' ? '~' : ''}${p.rulYears}` : '—'}
                                </Text>
                                {p.rulYears != null && <Text fontSize="sm" fontWeight="700" color={muted}>years</Text>}
                                {p.band && <Badge colorScheme={p.band.color} borderRadius="full">{p.band.label}</Badge>}
                            </HStack>
                            <HStack spacing={2} mt={1} wrap="wrap">
                                {sourceBadge}
                                {p.live && <Badge colorScheme="green" fontSize="2xs">Live update</Badge>}
                                {fmtDateTime(p.predictedAt) && <Text fontSize="2xs" color={muted}>Predicted {fmtDateTime(p.predictedAt)}</Text>}
                            </HStack>
                        </Box>
                    </HStack>

                    <SimpleGrid columns={2} spacing={3} minW="220px">
                        <Box>
                            <HStack spacing={0}><Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">Risk index</Text><FieldHint metric="risk_index" /></HStack>
                            <HStack spacing={1.5}>
                                <Text fontSize="xl" fontWeight="900">{p.riskScore}</Text>
                                <Badge colorScheme={p.riskLevel.color}>{p.riskLevel.label}</Badge>
                            </HStack>
                        </Box>
                        <Box>
                            <HStack spacing={0}><Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">Confidence</Text><FieldHint metric="confidence" /></HStack>
                            {p.confidence != null ? (
                                <HStack spacing={1.5}>
                                    <Text fontSize="xl" fontWeight="900">{Math.round(p.confidence * 100)}%</Text>
                                    <Badge colorScheme={p.confidenceInfo.color}>{p.confidenceInfo.label}</Badge>
                                </HStack>
                            ) : <Text fontSize="sm" color={muted} mt={1}>Not reported</Text>}
                        </Box>
                        <Box>
                            <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">Rehab needed by</Text>
                            <Text fontSize="sm" fontWeight="800">{fmtDate(p.failureDate)}</Text>
                        </Box>
                        <Box>
                            <HStack spacing={0}><Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">Data readiness</Text><FieldHint metric="readiness" /></HStack>
                            <HStack spacing={1.5}>
                                <Text fontSize="sm" fontWeight="800">{p.readiness.score}%</Text>
                                <Badge colorScheme={p.readiness.grade.color} fontSize="2xs">{p.readiness.grade.label}</Badge>
                            </HStack>
                        </Box>
                    </SimpleGrid>
                </Flex>

                <Box mt={3}><LifeBar prediction={p} /></Box>
                {p.lower != null && p.upper != null && (
                    <Text fontSize="2xs" color={muted}>Shaded range: model uncertainty {p.lower}–{p.upper} years.</Text>
                )}
                <Text fontSize="sm" mt={2}>{p.summary}</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                {/* Why — risk drivers */}
                <Box p={4} borderTopWidth="1px" borderColor={border}>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Why this risk score</Text>
                    <VStack align="stretch" spacing={1.5}>
                        {p.drivers.map((d, i) => (
                            <Box key={i}>
                                <Flex justify="space-between" fontSize="xs">
                                    <Text fontWeight="700">{d.label}</Text>
                                    <Text fontFamily="mono" color={muted}>+{Math.round(d.points)}</Text>
                                </Flex>
                                <Progress value={Math.min(100, d.points * 1.6)} size="xs" colorScheme={p.riskLevel.color} borderRadius="full" />
                                <Text fontSize="2xs" color={muted}>{d.detail}</Text>
                            </Box>
                        ))}
                    </VStack>
                    {p.features.length > 0 && (
                        <Box mt={3}>
                            <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={muted} mb={1}>Model's strongest factors</Text>
                            <HStack spacing={1} wrap="wrap">
                                {p.features.map(f => (
                                    <Badge key={f.feature} colorScheme="purple" variant="subtle" fontSize="2xs">
                                        {String(f.feature).replace(/_/g, ' ')}{f.importance != null ? ` ${Math.round(f.importance * (f.importance <= 1 ? 100 : 1))}%` : ''}
                                    </Badge>
                                ))}
                            </HStack>
                        </Box>
                    )}
                </Box>

                {/* What to do */}
                <Box p={4} borderTopWidth="1px" borderLeftWidth={{ base: 0, md: '1px' }} borderColor={border}>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Recommended actions</Text>
                    <VStack align="stretch" spacing={2}>
                        {p.actions.map((a, i) => (
                            <Flex key={`${a.intent}-${i}`} gap={2} align="flex-start" p={2} borderRadius="lg" bg={subtle}>
                                <Icon as={INTENT_ICON[a.intent] || MdBolt} color={`${PRIORITY_SCHEME[a.priority] || 'gray'}.400`} mt={0.5} />
                                <Box flex={1} minW={0}>
                                    <HStack spacing={1.5} wrap="wrap">
                                        <Text fontSize="xs" fontWeight="800">{a.label}</Text>
                                        <Badge colorScheme={PRIORITY_SCHEME[a.priority] || 'gray'} fontSize="9px">{a.priority}</Badge>
                                        {a.fromModel && <Badge colorScheme="purple" fontSize="9px">ML</Badge>}
                                    </HStack>
                                    <Text fontSize="2xs" color={muted}>{a.explain}</Text>
                                </Box>
                                {onAction && (
                                    <Button size="xs" variant="outline" colorScheme="purple" flexShrink={0}
                                        isLoading={a.intent === ACTION_INTENTS.RUN_AI && isRunningAI}
                                        onClick={() => onAction(a.intent, a)}>
                                        {a.intent === ACTION_INTENTS.CREATE_WO ? 'Create WO'
                                            : a.intent === ACTION_INTENTS.SCHEDULE_INSPECTION ? 'Schedule'
                                            : a.intent === ACTION_INTENTS.ASSESS ? 'Assess'
                                            : a.intent === ACTION_INTENTS.MAP ? 'Map'
                                            : a.intent === ACTION_INTENTS.RUN_AI ? 'Run AI'
                                            : a.intent === ACTION_INTENTS.EDIT ? 'Edit' : 'Open'}
                                    </Button>
                                )}
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            </SimpleGrid>

            {/* Inputs + model insights */}
            {(showInputs || p.insights.length > 0) && (
                <Box p={4} borderTopWidth="1px" borderColor={border}>
                    {showInputs && (
                        <>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>What the AI looks at</Text>
                            <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={2}>
                                {p.inputs.map(inp => (
                                    <Box key={inp.key} p={2} borderRadius="lg" bg={subtle} borderWidth="1px" borderColor={inp.value ? 'transparent' : 'orange.200'}>
                                        <HStack spacing={0}><Text fontSize="2xs" color={muted} fontWeight="700">{inp.label}</Text><FieldHint field={inp.field} /></HStack>
                                        <Text fontSize="xs" fontWeight="800" color={inp.value ? undefined : 'orange.400'} textTransform="capitalize" noOfLines={1}>
                                            {inp.value || 'Missing'}
                                        </Text>
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </>
                    )}
                    {p.insights.length > 0 && (
                        <Box mt={showInputs ? 3 : 0}>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Model insights for this road</Text>
                            <VStack align="stretch" spacing={1.5}>
                                {p.insights.slice(0, 4).map((ins, i) => {
                                    const sev = String(ins.severity || ins.type || 'info').toLowerCase();
                                    return (
                                        <HStack key={ins.id || i} align="flex-start" spacing={2}>
                                            <Icon as={INSIGHT_ICON[sev] || MdOutlineTipsAndUpdates} color={INSIGHT_COLOR[sev] || 'blue.400'} mt={0.5} />
                                            <Box>
                                                <Text fontSize="xs" fontWeight="800">{ins.title || 'AI insight'}</Text>
                                                {ins.message && <Text fontSize="2xs" color={muted}>{ins.message}</Text>}
                                            </Box>
                                            {ins.confidence != null && (
                                                <Badge fontSize="9px" ml="auto" flexShrink={0}>{Math.round((ins.confidence <= 1 ? ins.confidence * 100 : ins.confidence))}%</Badge>
                                            )}
                                        </HStack>
                                    );
                                })}
                            </VStack>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
});
RoadAiPredictionDetail.displayName = 'RoadAiPredictionDetail';

export default RoadAiPredictionDetail;